//#region libraries
import React, { useEffect, useState, useRef } from "react";

// custom components
import TimelineJobs from "../job-components/timeline-jobs";
import EmployeeList from "../employee-components/employee-list";
import TimelineHourRow from "./timeline-hour-row";
import TimelineTitles from "./timeline-titles";
import JobListToolbar from "../job-components/job-list-toolbar";
import { ToolTippy } from '_molecules';
import TimelineModals from "./timeline-modals";

// styling
import "./timeline-component.css"

// bootstrap
import { Col, Row } from "react-bootstrap";

// redux
import { useSelector, useDispatch, batch } from "react-redux";
import {
  getEmployeeList,
  setEmployeeSearchFilter,
  resetUpdateEmployeeStatus,
  selectUpdateEmployeeStatus,
  selectAllEmployees,
  setEmployeeConversations,
  setSelectedForChat,
  setEmployeeMessages,
  selectOpenBroadcastMessages,
  setOpenBroadcastMessages,
  selectClickedEmployeeId,
  setClickedEmployeeId
} from "_redux/slices/timeline/employees";
import {
  updateTaskList,
  resetTimelineToCache,
  resetTimelineData,
  getTaskList,
  selectDeleteTaskStatus,
  resetDeleteFlags,
  resetBookingByFlightNum,
  setBookingSearch,
  setBookingSearchFilter,
  updateArrivalsTaskList,
  updateDeparturesTaskList,
  selectArrivalsTaskList,
  selectDeparturesTaskList,
  selectBreakTaskList,
  updateBreakTaskList,
  selectAdhocTaskList,
  updateAdhocTaskList,
  resetPaxCheckInFlags,
  resetPaxCheckOutFlags,
  resetSetBookingLocationFlags,
  selectTimelineDataIndexes,

} from "_redux/slices/timeline/timeline-data";
import { selectFirebaseInfo, selectIata } from "_redux/slices/main-screen/user";
import {
  reset as resetSelectedTask,
  resetBreakTaskFlags,
  setSelectedFlightToView,
  resetSelectedTasksList,

} from "_redux/slices/timeline/selected-task";
import { getAirportLocation, getTimelineAreas, selectBookingType, selectSelectedTimeline } from "_redux/slices/timeline/timeline-areas";
import {
  openPaxCheckInModal,
  reset as resetAllMenuContext,
  resetFlightContext,
} from "_redux/slices/utils/menu-context";
import { setDashboardTaskList } from "_redux/slices/timeline/timeline-dashboard";
import {
  setSearchedFlight,
  setSelectedFlightToChat,
  resetPassengerFlightList
} from '_redux/slices/timeline/flight-information-list';
import { selectUsedTheme } from '_redux/slices/timeline/theme';
import { setEmployeeLocations } from "_redux/slices/timeline/live-location-tab";
import { setEmployeeBeacons, selectEmployeeBeacons } from "_redux/slices/timeline/employee-beacons";
import {
  getFlights,
  selectStatus as selectGetTimelineFlightsStatus,
  selectFlightSlotLookup
} from "_redux/slices/timeline/timeline-flight-list";
import {
  getBookings as getTimelineBookings,
  setTaskIds,
  selectStatus as selectGetTimelineBookingStatus,
  setRampFlightIds,
  setRampMarshallFlightIds,
} from "_redux/slices/timeline/timeline-bookings";
import { setEmpBreakSummary, setBreakTaskQueryRange, resetBreakTasks } from "_redux/slices/timeline/break-tasks";
//#endregion

//#region custom components

/* timeline */
import TimelineContainer from "./timeline-container";
import TimelineCard from "./timeline-card";
import TimelineContentContainer from "./timeline-content-container";
import TimelineToolbar from "./timeline-toolbar";
import AirportLocationsPanel from "../../../../components/organisms/timeline-dashboard/airport-locations-panel";

/* job list */
import JobListContainer from "../job-components/job-list-container";

// firestore
import { firestore } from "_utils/firebase-app";
import { collection, onSnapshot, query, where } from "firebase/firestore";

// functions 
import { getDate } from "_utils/functions";
import { EmployeeInformationColumnWidthRaw } from "../timeline-constants";
import TimelineSidebar from "./timeline-sidebar";
import {
  selectPreBoard,
  selectShowTimelineSidebar,
  selectTimelineDate,
  setShowTimelineSidebar,
  selectExpandLocations,
  selectThirtyMinLayout,
  setShowAllFlights,
  setTimeLineDate,
  selectRampBookings,
  increaseTimelineSlots,
  increaseTimelineStart,
  selectTimelineIndex,
  resetTimelineSlots,
  selectShowAllFlights,
  setTouchScreen,
  selectTouchScreen,
  setDefaultArrivalCheckInLocation,
  selectDefaultArrivalCheckInLocation,
  selectRampBookingsForAllLocations,
  selectExpandLiveMap,
  selectRampBookingsForHCAndHS,
  selectTimelineLeftWidthPercent,
  setTimelineLeftWidthPercent,
  selectTimelineTopHeightVh,
  setTimelineTopHeightVh,
  selectLocationsTableView,
} from "_redux/slices/timeline/timeline-control";


//#endregion

// utils
/**
 * Calculate timeline start and end dates from timeIndex (without using getDate)
 * @param {string|Date} timelineDate - The base timeline date
 * @param {string[]} timeIndex - Array of time slots (e.g., ["08:00", "08:30", ...])
 * @param {boolean} thirtyMinLayout - Whether using 30-minute or hourly layout
 * @returns {{startDate: string, endDate: string} | null} - Date range or null if invalid
 */
const calculateTimelineDateRange = (timelineDate, timeIndex, thirtyMinLayout) => {

  // Guard: Check if timeIndex exists and has elements
  if (!Array.isArray(timeIndex) || timeIndex.length === 0) {
    return null;
  }

  // Get the base date (handle both string and Date inputs)
  let baseDate;
  if (timelineDate instanceof Date) {
    baseDate = new Date(timelineDate.getFullYear(), timelineDate.getMonth(), timelineDate.getDate());
  } else if (typeof timelineDate === 'string') {
    const dateObj = new Date(timelineDate);
    baseDate = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
  } else {
    return null;
  }

  // Check if the first slot is actually from yesterday
  // This happens when viewing "today" but timeline extends back before midnight (e.g., it's 1am and showing from 11pm)
  const now = new Date();
  const isToday = baseDate.toDateString() === now.toDateString();
  const [firstSlotHour] = timeIndex[0].split(':').map(Number);
  const currentHour = now.getHours();

  // If viewing today and first slot hour is much greater than current hour, it's from yesterday
  // e.g., current hour is 1 (1am), first slot is 22 (10pm) → 22 > 1 + 4 → first slot is yesterday
  const shiftedYesterday = isToday && firstSlotHour > currentHour + 4;
  if (shiftedYesterday) {
    baseDate = new Date(baseDate.getTime() - 24 * 60 * 60 * 1000);
  }

  // Helper to format date as YYYY-MM-DD in LOCAL time (not UTC)
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Create function to get correct date for a time slot (handles midnight crossing)
  const createDateTimeString = (timeString) => {
    const [hours] = timeString.split(':').map(Number);
    const [firstHours] = timeIndex[0].split(':').map(Number);

    // If this time slot's hour is less than the first time slot's hour, it means we crossed midnight
    // Use the next day for times that wrapped around
    const slotDate = hours < firstHours ? new Date(baseDate.getTime() + 24 * 60 * 60 * 1000) : baseDate;

    // Format as YYYY-MM-DD in LOCAL time
    const dateStr = formatLocalDate(slotDate);
    return `${dateStr}T${timeString}:00`;
  };

  // Start date is the first time slot
  const firstTimeSlot = timeIndex[0];
  if (!firstTimeSlot || typeof firstTimeSlot !== 'string') {
    return null;
  }
  const startDate = createDateTimeString(firstTimeSlot);

  // End date is the last time slot + slot duration
  const lastTimeSlot = timeIndex[timeIndex.length - 1];
  if (!lastTimeSlot || typeof lastTimeSlot !== 'string') {
    return null;
  }

  // Create the datetime for the last slot using the midnight-crossing logic
  const endDateTimeStr = createDateTimeString(lastTimeSlot);
  const endDateTime = new Date(endDateTimeStr);

  // Validate the created date is valid
  if (isNaN(endDateTime.getTime())) {
    return null;
  }

  // Add slot duration (30 minutes for 30-min layout, 60 minutes for hourly layout)
  const slotDuration = thirtyMinLayout ? 30 : 60;
  endDateTime.setMinutes(endDateTime.getMinutes() + slotDuration);

  // Format as YYYY-MM-DDTHH:mm:ss in LOCAL time
  const endDateStr = formatLocalDate(endDateTime);
  const endHours = String(endDateTime.getHours()).padStart(2, '0');
  const endMinutes = String(endDateTime.getMinutes()).padStart(2, '0');
  const endDate = `${endDateStr}T${endHours}:${endMinutes}:00`;

  // Final validation of datetime strings
  const dateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;
  if (!dateTimeRegex.test(startDate) || !dateTimeRegex.test(endDate)) {
    return null;
  }

  return { startDate, endDate };
};

const TimelineComponent = () => {

  // ref for screenshot capture
  const timelineMainRef = useRef(null);

  //#region states
  const [clickedColumn, setClickedColumn] = useState(false);                             // holds state of column click
  const [timelineHeight, setTimelineHeight] = useState(0)                                // used to monitor height of timeline (timeline card + job list)
  const [employeeIdsToListenTo, setEmployeeIdsToListenTo] = useState([]);                // holds the employee ids to listen to for conversations
  const beaconsRef = useRef([]);
  const modalsRef = useRef(null);

  //#endregion

  //#region redux
  const dispatch = useDispatch();

  const dbUser = useSelector(selectFirebaseInfo);                                        // logged in user
  const iata = useSelector(selectIata);                                                  // logged-in user IATA
  const expandLiveMap = useSelector(selectExpandLiveMap);
  const deleteTaskStatus = useSelector(selectDeleteTaskStatus);
  const arrivalsTaskList = useSelector(selectArrivalsTaskList);
  const departuresTaskList = useSelector(selectDeparturesTaskList);
  const timelineDate = useSelector(selectTimelineDate);                                  // select timeline Date
  const preBoard = useSelector(selectPreBoard);                                         // status of auto accept unplanned task switch
  const rampBookings = useSelector(selectRampBookings);                                 // status of ramp bookings switch
  const rampBookingsForAllLocations = useSelector(selectRampBookingsForAllLocations); // status of RampBookingsForAllLocations switch
  const rampBookingsForHCAndHS = useSelector(selectRampBookingsForHCAndHS); // status of RampBookingsForHCAndHS switch

  const showAllFlights = useSelector(selectShowAllFlights);
  const breakTaskList = useSelector(selectBreakTaskList);
  const adhocTaskList = useSelector(selectAdhocTaskList);
  const allEmployees = useSelector(selectAllEmployees);
  const updateEmployeeStatus = useSelector(selectUpdateEmployeeStatus);                  // holds the value of update Employee action status
  const bookingType = useSelector(selectBookingType);                                    // select bookingType (departures,arrivals,all)

  const showTimelineSidebar = useSelector(selectShowTimelineSidebar);                    // holds value to show timeline sidebar
  const thirtyMinLayout = useSelector(selectThirtyMinLayout);
  const expandLocations = useSelector(selectExpandLocations);                            // holds value to show/hide airport locations panel
  const locationsTableView = useSelector(selectLocationsTableView);
  const timeIndex = useSelector(selectTimelineIndex);
  const clickedEmployeeId = useSelector(selectClickedEmployeeId)

  // employee beacons
  const employeeBeacons = useSelector(selectEmployeeBeacons);


  // theme
  const usedTheme = useSelector(selectUsedTheme);

  // timeline booking

  // timeline flights
  const flightSlotLookup = useSelector(selectFlightSlotLookup);

  const selectedTimeline = useSelector(selectSelectedTimeline);

  // DELETE
  const defaultArrivalCheckInLocation = useSelector(selectDefaultArrivalCheckInLocation);

  //#endregion



  //#region functions


  // get timeline booking list
  const handleGetBookings = (jobsTypeFilter) => {

    // check if bookings are already being retrieved
    //if (getTimelineBookingStatus == 'loading') return;
    // get timeline date
    const date = getDate(timelineDate);
    const jobsType = jobsTypeFilter && Array.isFullArray(jobsTypeFilter)
      ? jobsTypeFilter.join("_")
      : Array.isFullArray(bookingType) ? bookingType.join("_") : "all";

    // Get the date range from timeIndex
    const dateRange = calculateTimelineDateRange(timelineDate, timeIndex, thirtyMinLayout);
    if (!dateRange) {
      return;
    }

    const { startDate, endDate } = dateRange;

    // convert to YYYY-MM-DD
    //const startDateYYYYMMDD = new Date(startDate)?.toISOString().split('T')[0];
    //const endDateYYYYMMDD = new Date(endDate)?.toISOString().split('T')[0];


    // get timeline bookings from serverj4
    dispatch(getTimelineBookings({
      // iata
      iata: dbUser.details.authority,

      // date range - now using precise time range from timeline
      //startDate: startDateYYYYMMDD,
      //endDate: endDateYYYYMMDD,
      startDate: startDate,
      endDate: endDate,

      // preboard
      boardingBookings: preBoard,
      jobsType: jobsType
    }));

  }



  // get flight list
  const handleGetFlights = (jobsTypeFilter) => {

    // check if flights are already being retrieved
    //if (getTimelineFlightsStatus == 'loading') return;
    // get timeline date
    const jobsType = jobsTypeFilter && Array.isFullArray(jobsTypeFilter)
      ? jobsTypeFilter.join("_")
      : Array.isFullArray(bookingType) ? bookingType.join("_") : "all";

    // Get the date range from timeIndex
    const dateRange = calculateTimelineDateRange(timelineDate, timeIndex, thirtyMinLayout);
    if (!dateRange) {
      return;
    }

    const { startDate, endDate } = dateRange;

    // get timeline flights from server
    dispatch(getFlights({
      // iata
      iata: dbUser.details.authority,

      // date range - now using precise time range from timeline
      startDate: startDate,
      endDate: endDate,

      // type
      type: "all",

      // group by time
      groupByTime: true,

      // layout
      thirtyMinLayout: thirtyMinLayout,
      //thirtyMinLayout: true,

      // preboard
      boardingFlights: preBoard,

      selectedAirportArea: selectedTimeline?._id ?? "",
      rampTasks: rampBookings,
      showAllFlights: showAllFlights,
      jobsType: jobsType,
      holdingArea: selectedTimeline?.holdingArea ?? false,
      holdingLocation: selectedTimeline?.holdingLocation?.locationId ?? "",
      rampForAllLocs: rampBookingsForAllLocations,
      rampForHCAndHS: rampBookingsForHCAndHS,
    }))

  }
  //#endregion

  //#region use effects
  // on render reset all flags — batch to coalesce into a single re-render
  useEffect(() => {
    batch(() => {
      dispatch(getTimelineAreas(dbUser.details.authority));
      dispatch(getAirportLocation(dbUser.details.authority));
      dispatch(resetTimelineSlots({ authority: dbUser?.details?.authority }));
      dispatch(resetTimelineData());
      dispatch(resetSelectedTasksList());
      dispatch(resetSelectedTask());
      dispatch(getEmployeeList(dbUser.details.authority));
      dispatch(getTaskList(dbUser.details.authority));
      dispatch(resetBookingByFlightNum());
      dispatch(setBookingSearch(""));
      dispatch(setBookingSearchFilter('flight'));
      dispatch(setEmployeeSearchFilter('name'));
      dispatch(setSearchedFlight(""));
      dispatch(resetFlightContext());
      dispatch(setSelectedFlightToView(null));
      dispatch(setSelectedForChat(null));
      dispatch(resetBreakTaskFlags());
      dispatch(openPaxCheckInModal(false));
      dispatch(resetPaxCheckInFlags());
      dispatch(resetPaxCheckOutFlags());
      dispatch(setShowTimelineSidebar(false));
      dispatch(resetAllMenuContext());
      dispatch(resetSetBookingLocationFlags());
      dispatch(setTimeLineDate(new Date()));
      dispatch(setSelectedFlightToChat(null));
      dispatch(resetPassengerFlightList());
    });
    scrollToPosition();
  }, []);

  // Midnight detection: auto-updates timelineDate when the calendar day rolls over.
  // Without this, timelineDate stays stuck on the previous day, causing
  // selectTimelineIndex to fall into "full day" mode and flood the timeline
  // with all of yesterday's tasks, flights, and break data.
  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = new Date();
      const todayStr = getDate(now);
      const timelineDateStr = getDate(timelineDate);

      if (timelineDateStr === todayStr) return;

      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = getDate(yesterday);

      if (timelineDateStr === yesterdayStr) {
        dispatch(setTimeLineDate(new Date()));
      }
    }, 60000);

    return () => clearInterval(intervalId);
  }, [timelineDate]);

  // Auto-expand timeline if content doesn't fill the viewport (ultrawide monitors)
  useEffect(() => {
    const el = jobListRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      const containerWidth = el.clientWidth;
      const columnWidth = EmployeeInformationColumnWidthRaw;
      const slotsPerHour = thirtyMinLayout ? 2 : 1;
      const pixelsPerHour = columnWidth * slotsPerHour;

      const minHoursNeeded = Math.ceil(containerWidth / pixelsPerHour) + 3;
      const defaultHours = 10;

      if (minHoursNeeded > defaultHours) {
        dispatch(increaseTimelineSlots({
          hoursToAdd: minHoursNeeded - defaultHours,
          authority: dbUser?.details?.authority
        }));
      }
    });
  }, []);



  // get bookings + flights listener
  useEffect(() => {
    if (showTimelineSidebar) return;
    handleGetBookings(bookingType);
    handleGetFlights(bookingType);
  }, [timelineDate, showTimelineSidebar, timeIndex]);

  // periodic refresh for flight data (gate/stand updates) - every 60 seconds
  // useEffect(() => {
  //   if (showTimelineSidebar) return;

  //   const intervalId = setInterval(() => {
  //     handleGetFlights(bookingType);
  //   }, 60000); // refresh every 60 seconds

  //   return () => clearInterval(intervalId);
  // }, [timelineDate, timeIndex, showTimelineSidebar]);

  // subscribe to employee conversations
  useEffect(() => {
    if (!dbUser?.details?.authority) return;

    const iata = dbUser.details.authority;
    const onlineEmployees = allEmployees?.filter((emp) => emp?.status !== "offline")?.map((emp) => emp?.employeeId);
    // Map online employee userId -> employeeId (lastMessage.senderId is Firebase UID)
    const onlineByUserId = {};
    allEmployees?.filter((emp) => emp?.status !== "offline")?.forEach((emp) => {
      if (emp?.employeeId) onlineByUserId[emp.employeeId] = emp.employeeId;
    });

    if (!onlineEmployees || onlineEmployees.length === 0) {
      dispatch(setEmployeeConversations([]));
      setEmployeeIdsToListenTo([]);
      return;
    }

    const empConversationsCollectionRef = collection(firestore, "employeeConversations");

    // Firestore limits array-contains-any to 30 values, so we need to batch
    const BATCH_SIZE = 30;
    const batches = [];

    for (let i = 0; i < onlineEmployees.length; i += BATCH_SIZE) {
      batches.push(onlineEmployees.slice(i, i + BATCH_SIZE));
    }

    // Store results per batch; merge and dispatch when any batch updates
    const batchResults = {};
    const totalBatches = batches.length;

    const mergeAndDispatch = () => {
      const allConversations = [];
      for (let i = 0; i < totalBatches; i++) {
        if (Array.isArray(batchResults[i])) {
          allConversations.push(...batchResults[i]);
        }
      }
      // Dedupe by conversation id (same doc can match multiple batches)
      const seen = new Set();
      const merged = allConversations.filter((c) => {
        const id = c.id ?? c._id ?? c.docId ?? null;
        if (id == null) return true;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      dispatch(setEmployeeConversations(Array.isFullArray(merged) ? merged : []));

      // lastMessage.status === "sent" = unread; if sender is an online employee, add their employeeId to state
      const idsToListenTo = [];

      merged.forEach((conv) => {
        const lastMsg = conv?.lastMessage;
        if (lastMsg?.status !== "sent") return;
        const senderId = lastMsg?.senderId;
        if (!senderId) return;
        const employeeId = onlineByUserId[senderId];
        if (employeeId && !idsToListenTo.includes(employeeId)) {
          idsToListenTo.push(employeeId);
        }

      });
      setEmployeeIdsToListenTo(idsToListenTo);
    };

    const unsubscribers = [];

    batches.forEach((batch, batchIndex) => {
      const conversationsQuery = query(
        empConversationsCollectionRef,
        where("iata", "==", iata),
        where("participantIds", "array-contains-any", batch)
      );

      const unsubscribe = onSnapshot(conversationsQuery, (querySnapshot) => {
        const conversations = [];
        querySnapshot.forEach((doc) => {
          conversations.push({ ...doc.data(), id: doc.id });
        });
        batchResults[batchIndex] = conversations;
        mergeAndDispatch();
      });

      unsubscribers.push(unsubscribe);
    });

    return () => unsubscribers.forEach((unsub) => unsub());
  }, [allEmployees, dbUser?.details?.authority]);

  // employee location listener
  // Depends on timelineDate so the listener re-subscribes to the new date's - midnight fix
  useEffect(() => {

    if (!dbUser?.details?.authority) return;

    const iata = dbUser.details.authority;
    const dateObj = timelineDate instanceof Date ? timelineDate : new Date(timelineDate);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;

    const employeeLocationsRef = collection(
      firestore,
      "employeesLocations",
      iata,
      formattedDate
    );

    const unsubscribe = onSnapshot(employeeLocationsRef, (snapshot) => {
      const updatedEmployeeLocations = [];

      snapshot.forEach((doc) => {
        updatedEmployeeLocations.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      dispatch(setEmployeeLocations(updatedEmployeeLocations));
    });

    return () => unsubscribe();
  }, [dbUser.details.authority, timelineDate]);

  // subscribe to employee beacons
  useEffect(() => {
    beaconsRef.current = employeeBeacons;
  }, [employeeBeacons]);

  // listener for employeeBeacons firestore data
  // Depends on timelineDate so the listener re-subscribes to the new date's - midnight fix
  useEffect(() => {
    beaconsRef.current = employeeBeacons;

    const dateObj = timelineDate instanceof Date ? timelineDate : new Date(timelineDate);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const date = `${year}-${month}-${day}`;

    const colRef = collection(
      firestore,
      "employeeBeacons",
      dbUser.details.authority,
      date
    );

    const unsubscribe = onSnapshot(colRef, (snapshot) => {

      // Get ALL documents
      const allEmployees = snapshot.docs.map(doc => {
        const data = doc.data();

        return {
          id: doc.id,
          empName: data.empName,
          employeeId: data.employeeId,
          iata: data.iata,
          latestBeacon: {
            ...data.latestBeacon,
          },
        };
      });

      dispatch(setEmployeeBeacons(allEmployees));
    }, (err) => {
      console.error("Listener error:", err);
    });

    return () => unsubscribe();

  }, [dbUser.details.authority, timelineDate]);

  // subscribe to tasks changes
  useEffect(() => {
    const arrivalTasks = [];
    const departureTasks = [];
    const breakTasks = [];
    const adhocTasks = [];

    let arrivalIds = [];
    let departureIds = [];
    let breakIds = [];
    let adhocIds = [];
    let rampFlightIds = [];
    let rampMarshallFlightIds = [];
    // Get the same date range used for flights and bookings
    const dateRange = calculateTimelineDateRange(timelineDate, timeIndex, thirtyMinLayout);
    if (!dateRange) {
      return; // Exit early if date range calculation fails
    }

    const { startDate, endDate } = dateRange;
    const formatDateForQuery = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      const hh = String(date.getHours()).padStart(2, "0");
      const mm = String(date.getMinutes()).padStart(2, "0");
      const ss = String(date.getSeconds()).padStart(2, "0");
      return `${y}-${m}-${d}T${hh}:${mm}:${ss}`;
    };
    const startDateForTasks = formatDateForQuery(new Date(new Date(startDate).getTime() - (2 * 60 * 60 * 1000)));
    const endDateForTasks = formatDateForQuery(new Date(new Date(endDate).getTime() + (4 * 60 * 60 * 1000)));
    const taskCollectionRef = collection(firestore, "tasks");

    // Query tasks within the exact timeline date range
    const arrivalQuery = query(
      taskCollectionRef,
      where("flightInformation.arrivalAirport", "==", dbUser.details.authority),
      where("flightInformation.flightTime", ">=", startDateForTasks),
      where("flightInformation.flightTime", "<=", endDateForTasks)
    );

    const departureQuery = query(
      taskCollectionRef,
      where("flightInformation.departureAirport", "==", dbUser.details.authority),
      where("flightInformation.flightTime", ">=", startDateForTasks),
      where("flightInformation.flightTime", "<=", endDateForTasks)
    );

    // For break tasks, we need to query by date range since they use date fields
    // Convert startDate and endDate to date strings for break task queries
    const formatLocalYYYYMMDD = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };

    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);

    // normalize both to local midnight   e)
    const startDay = new Date(startDateObj);
    startDay.setHours(0, 0, 0, 0);

    const endDay = new Date(endDateObj);
    endDay.setHours(0, 0, 0, 0);

    const breakTaskDates = [];
    for (let d = new Date(startDay); d <= endDay; d.setDate(d.getDate() + 1)) {
      breakTaskDates.push(formatLocalYYYYMMDD(d));
    }

    // Create queries for each date in the range
    const breakTaskQueries = breakTaskDates.map(dateStr => query(
      taskCollectionRef,
      where("breakTaskInformation.airport", "==", dbUser.details.authority),
      where("breakTaskInformation.breakTaskDate", "==", dateStr)
    ));

    const adhocTaskQueries = breakTaskDates.map(dateStr => query(
      taskCollectionRef,
      where("isAdhocTask", "==", true),
      where("adhocTaskInformation.assignedDate", "==", dateStr),
      where("adhocTaskInformation.airport", "==", dbUser.details.authority)
    ));

    //console.log('[Adhoc Tasks] Setting up listeners for dates:', breakTaskDates);
    //console.log('[Adhoc Tasks] Authority:', dbUser.details.authority);

    const processSnapshot = (snapshot, taskArr, idArr, isFor, rampFIDS, rampMarshallFIDS) => {
      taskArr.length = 0;
      idArr.length = 0;
      if (rampFIDS) rampFIDS.length = 0;
      if (rampMarshallFIDS) rampMarshallFIDS.length = 0;


      snapshot.forEach((doc) => {

        let data = doc.data();

        // check if self allocated / group task from app

        // collect id
        const rawId = data._id;
        if (data?.flightInformation?.iata && data?.flightInformation?.iata !== iata) return;
        const cleanedId = rawId.replace(/_IATA.*$/, "");
        idArr.push(cleanedId);
        if (data?.flightInformation?.arrivalAirport === iata) {
          idArr?.push(`ARR_${cleanedId}`)
          // in case of created from employee app, 
          if (data?.createdFromEmployeeApp && !data?._id?.startsWith("ARR_")) {
            idArr?.push(`ARR_${cleanedId?.split("_")?.slice(1)?.join("_")}`)
          }
        }


        // if data includes passengers array and is not combined booking or combined ramp booking
        if ((Array.isFullArray(data?.passengers)) && (!data?.combinedBooking && !data?.combinedRampBooking)) {
          data?.passengers?.forEach((pax) => {
            if (pax?.originalBookingId) idArr.push(pax?.originalBookingId);
          });
        }

        if (data?.combinedRampBooking && !data?.combinedMarshallBooking && !data?._id?.includes("-duplicate") && rampFIDS) {
          rampFIDS.push(data?.flightId)
        }

        if (data?.combinedMarshallBooking && data?.combinedRampBooking && !data?._id?.includes("-duplicate") && rampMarshallFIDS) {
          rampMarshallFIDS.push(data?.flightId)
        }
        if (data?.flightBooking && !data?.combinedBooking && !data?.combinedRampBooking && !data?.flightTask && data?.originalArrBookingId) {

        }
        // skip estimated or combined
        if ((!preBoard && data.isEstimatedBooking) || (!preBoard && data.combinedBooking)) {
          return;
        }

        taskArr.push(data);
      });

    };

    const updateAllIds = () => {
      const merged = [
        ...new Set(arrivalIds),
        ...departureIds,
        ...breakIds,
        ...adhocIds
      ];

      //check each id in update all ids 
      dispatch(setTaskIds(merged));
    };

    const updateRampFlightIds = () => {
      dispatch(setRampFlightIds([...new Set(rampFlightIds)]));
    };

    const updateRampMarshallFlightIds = () => {
      dispatch(setRampMarshallFlightIds([...new Set(rampMarshallFlightIds)]));
    };

    const unsubArrival = onSnapshot(arrivalQuery, (snap) => {
      processSnapshot(snap, arrivalTasks, arrivalIds, "arrival", rampFlightIds, rampMarshallFlightIds);
      dispatch(updateArrivalsTaskList([...arrivalTasks]));
      updateAllIds();
      updateRampFlightIds();
      updateRampMarshallFlightIds();
    });

    const unsubDeparture = onSnapshot(departureQuery, (snap) => {
      processSnapshot(snap, departureTasks, departureIds, "departure");
      dispatch(updateDeparturesTaskList([...departureTasks]));
      updateAllIds();
    });

    // Set up listeners for each break task query
    const unsubBreakTasks = breakTaskQueries.map((query, index) => {
      return onSnapshot(query, (snap) => {
        // Process break tasks for this specific date
        const tasksForDate = [];
        const idsForDate = [];

        snap.forEach((doc) => {
          let data = doc.data();
          const rawId = data._id;
          const cleanedId = rawId.replace(/_IATA.*$/, "");
          idsForDate.push(cleanedId);

          // Add passengers' booking IDs if applicable
          if (Array.isFullArray(data?.passengers) && (!data?.combinedBooking && !data?.combinedRampBooking)) {
            data?.passengers?.forEach((pax) => {
              if (pax?.originalBookingId) idsForDate.push(pax?.originalBookingId);
            });
          }

          // Skip estimated or combined
          if ((!preBoard && data.isEstimatedBooking) || (!preBoard && data.combinedBooking)) {
            return;
          }

          // Skip break tasks that fall before the visible window start
          const breakStartTime = data?.breakTaskInformation?.breakStartTime ?? data?.startTime;
          if (breakStartTime) {
            const breakStartMs = breakStartTime?.toDate ? breakStartTime.toDate().getTime() : (breakStartTime?.seconds ?? 0) * 1000;
            if (breakStartMs && breakStartMs < startDateObj.getTime()) return;
          }

          tasksForDate.push(data);
        });

        // Update the global break tasks state by merging with existing data
        // Use a Map to avoid duplicates based on task ID
        const breakTasksMap = new Map();
        const currentDateStr = breakTaskDates[index];

        // Add existing break tasks to the map, but skip merging in-memory tasks for the current date 
        breakTasks.forEach(task => {
          if (task?.breakTaskInformation?.breakTaskDate !== currentDateStr) {
            breakTasksMap.set(task._id, task);
          }
        });

        // Add new tasks for this date to the map (will overwrite if same ID)
        tasksForDate.forEach(task => {
          breakTasksMap.set(task._id, task);
        });

        // Convert back to array
        const mergedBreakTasks = Array.from(breakTasksMap.values());

        // Update IDs set to avoid duplicates
        const mergedBreakIds = new Set([...breakIds, ...idsForDate]);

        // Update state
        breakTasks.length = 0;
        breakIds.length = 0;
        breakTasks.push(...mergedBreakTasks);
        Array.from(mergedBreakIds).forEach(id => breakIds.push(id));

        dispatch(updateBreakTaskList([...breakTasks]));
        updateAllIds();
      });
    });

    const adhocTasksByDate = {};
    const adhocIdsByDate = {};

    const unsubAdhocTasks = adhocTaskQueries.map((adhocQuery, index) => {
      return onSnapshot(adhocQuery, (snap) => {
        const tasksForDate = [];
        const idsForDate = [];

        snap.forEach((doc) => {
          let data = doc.data();
          const rawId = data._id;
          const cleanedId = rawId.replace(/_IATA.*$/, "");
          idsForDate.push(cleanedId);

          if (Array.isFullArray(data?.passengers) && (!data?.combinedBooking && !data?.combinedRampBooking)) {
            data?.passengers?.forEach((pax) => {
              if (pax?.originalBookingId) idsForDate.push(pax?.originalBookingId);
            });
          }

          // Skip adhoc tasks that fall before the visible window start
          const adhocStartTime = data?.startTime ?? data?.allocatedTime;
          if (adhocStartTime) {
            const adhocStartMs = adhocStartTime?.toDate ? adhocStartTime.toDate().getTime() : (adhocStartTime?.seconds ?? 0) * 1000;
            if (adhocStartMs && adhocStartMs < startDateObj.getTime()) return;
          }

          tasksForDate.push(data);
        });

        adhocTasksByDate[index] = tasksForDate;
        adhocIdsByDate[index] = idsForDate;

        const mergedAdhocTasks = Object.values(adhocTasksByDate).flat();
        const mergedAdhocIds = Object.values(adhocIdsByDate).flat();

        adhocTasks.length = 0;
        adhocIds.length = 0;
        adhocTasks.push(...mergedAdhocTasks);
        mergedAdhocIds.forEach(id => adhocIds.push(id));

        dispatch(updateAdhocTaskList([...adhocTasks]));
        updateAllIds();
      });
    });

    return () => {
      unsubArrival();
      unsubDeparture();
      unsubBreakTasks.forEach(unsub => unsub());
      unsubAdhocTasks.forEach(unsub => unsub());
      //unsubAdhocTasks.forEach(unsub => unsub());

      // Clear stale task data so old tasks don't flash while new listeners spin up
      dispatch(updateArrivalsTaskList([]));
      dispatch(updateDeparturesTaskList([]));
      dispatch(updateBreakTaskList([]));
    };

  }, [timelineDate, timeIndex, preBoard]);

  // update the task list whenever the arrivals TaskList and departures TaskList changes
  useEffect(() => {
    let tempArrivals = Array.isFullArray(arrivalsTaskList) ? arrivalsTaskList : [];
    let tempDepartures = Array.isFullArray(departuresTaskList) ? departuresTaskList : [];
    let tempBreakTask = Array.isFullArray(breakTaskList) ? breakTaskList : [];
    let tempAdhocTask = Array.isFullArray(adhocTaskList) ? adhocTaskList : [];
    dispatch(updateTaskList({
      tasks: [...tempArrivals, ...tempDepartures, ...tempBreakTask, ...tempAdhocTask],
      flightSlotLookup,
      userIata: iata,
      thirtyMinLayout
    }));
    dispatch(setDashboardTaskList([...tempArrivals, ...tempDepartures]))

  }, [arrivalsTaskList, departuresTaskList, breakTaskList, adhocTaskList, flightSlotLookup, thirtyMinLayout, iata])

  // delete task listener
  useEffect(() => {
    if (deleteTaskStatus == 'succeeded') {
      // reset menu context
      dispatch(resetAllMenuContext());
      // reset delete flags
      dispatch(resetDeleteFlags());
    }
  }, [deleteTaskStatus])

  // employee update listener
  useEffect(() => {
    // reset update employee status
    if (updateEmployeeStatus === "succeeded" || updateEmployeeStatus === "failed") {
      dispatch(resetUpdateEmployeeStatus())
    }
  }, [updateEmployeeStatus]);

  // for jersey automatically show all flights
  useEffect(() => {
    if (dbUser?.details?.authority) {
      if (dbUser?.details?.authority == 'JER') {
        dispatch(setShowAllFlights(true))
      }
    }
  }, [dbUser]);

  useEffect(() => {
    if (selectedTimeline?.holdingArea && selectedTimeline?.holdingLocation) {
      dispatch(setDefaultArrivalCheckInLocation(selectedTimeline?.holdingLocation?.locationId));
    } else if (defaultArrivalCheckInLocation) {
      dispatch(setDefaultArrivalCheckInLocation(defaultArrivalCheckInLocation));
    } else dispatch(setDefaultArrivalCheckInLocation(null));
  }, [selectedTimeline]);

  // subscribe to break tasks only: range current time - 12h till timeline index end time
  useEffect(() => {
    if (!dbUser?.details?.authority) return;
    if (showTimelineSidebar) return;
    if (!Array.isArray(timeIndex) || timeIndex.length === 0) return;

    // Range START = current time - 12 hours
    const now = new Date();
    const windowStart = new Date(now.getTime() - 12 * 60 * 60 * 1000);

    // Range END = end of timeline 
    const dateRange = calculateTimelineDateRange(timelineDate, timeIndex, thirtyMinLayout);
    if (!dateRange) return;

    const windowEnd = new Date(dateRange.endDate);
    if (isNaN(windowEnd.getTime())) return;

    dispatch(setBreakTaskQueryRange({
      start: windowStart.toISOString(),
      end: windowEnd.toISOString(),
    }));

    const taskCollectionRef = collection(firestore, "tasks");

    // YYYY-MM-DD in local time
    const formatLocalYYYYMMDD = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };

    // Build list of date keys from windowStart to windowEnd
    const startDay = new Date(windowStart);
    startDay.setHours(0, 0, 0, 0);

    const endDay = new Date(windowEnd);
    endDay.setHours(0, 0, 0, 0);

    const breakTaskDates = [];
    for (let d = new Date(startDay); d <= endDay; d.setDate(d.getDate() + 1)) {
      breakTaskDates.push(formatLocalYYYYMMDD(d));
    }

    // One query per day
    const breakTaskQueries = breakTaskDates.map((dateStr) =>
      query(
        taskCollectionRef,
        where("isBreakTask", "==", true),
        where("breakTaskInformation.airport", "==", dbUser.details.authority),
        where("breakTaskInformation.breakTaskDate", "==", dateStr)
      )
    );

    const docsById = new Map();

    const recomputeAndDispatch = () => {
      const byEmployeeId = {};

      // for each break task doc add it to byEmployeeId
      docsById.forEach((task) => {
        const empId = task?.employeeId;
        const empName = task?.breakTaskInformation?.employeeName ?? '';
        const breakStart = task?.breakTaskInformation?.breakStartTime;
        const breakEnd = task?.breakTaskInformation?.breakEndTime;

        if (!empId || !breakStart || !breakEnd) return;
        if (task?.cancelled === true) return;

        // Exclude break tasks that fall before windowStart — the Firestore query fetches the entire day, so for the partial day (yesterday) we trim here.
        const breakStartMs = breakStart?.toDate ? breakStart.toDate().getTime() : (breakStart?.seconds ?? 0) * 1000;
        if (breakStartMs && breakStartMs < windowStart.getTime()) return;

        if (!byEmployeeId[empId]) {
          byEmployeeId[empId] = {
            employeeId: empId,
            empName: empName,
            breakCount: 0,
            hasBreakTaken: false,
            breaks: [],
          };
        }

        byEmployeeId[empId].breakCount += 1;
        byEmployeeId[empId].hasBreakTaken = true;
        byEmployeeId[empId].breaks.push(task);
      });

      // dispatch to the slice
      dispatch(setEmpBreakSummary(byEmployeeId));
    };

    // subscribe to the break task dates
    const unsubscribers = breakTaskQueries.map((q) => onSnapshot(q, (snap) => {
      snap.docChanges().forEach((change) => {
        const docId = change.doc.id;
        const data = { id: docId, ...change.doc.data() };

        if (change.type === "removed") {
          docsById.delete(docId);
        } else {
          docsById.set(docId, data);
        }
      });

      recomputeAndDispatch();
    },
      (err) => {
        console.error("[break task listener] error:", err);
      }
    )
    );

    return () => {
      unsubscribers.forEach((unsub) => unsub());
      dispatch(resetBreakTasks());
    };
  }, [dbUser?.details?.authority, timelineDate, timeIndex, thirtyMinLayout, showTimelineSidebar]);

  useEffect(() => {
    if (!clickedColumn && clickedEmployeeId) {
      dispatch(setClickedEmployeeId(null))
    }
  }, [clickedColumn])
  //#endregion

  //#region scrollable functions
  const scrollableDivRef = useRef(null); // reference to employee list
  const jobListRef = useRef(null); // reference to job list
  const disableScrollSync = useRef(false);  // ref to disable handleEmployeeListScroll and handleJobListScroll when search auto scroll in timelineJobs is working 

  // move job list when employee list is scrolled
  const handleEmployeeListScroll = (event) => {
    if (disableScrollSync.current) return; // prevent sync during `scrollIntoView()`

    jobListRef.current.scrollLeft = event.target.scrollLeft;
  };

  // move employee list when job list is scrolled
  const handleJobListScroll = (event) => {
    if (disableScrollSync.current) return; //  prevent sync during `scrollIntoView()`

    scrollableDivRef.current.scrollLeft = event.target.scrollLeft;
  };

  // Function to scroll to a specific point
  const scrollToPosition = () => {

    // Calculate the desired scroll position (in pixels)
    let columnWidth = EmployeeInformationColumnWidthRaw;

    // calculate current hour
    const currentHour = new Date().getHours();
    const currentMinute = new Date().getMinutes();
    const currentSecond = new Date().getSeconds();

    let desiredScrollPosition = columnWidth * currentHour;

    const minuteWidth = columnWidth / 60;   // calculate the width of a minute on the timeline
    const secondWidth = minuteWidth / 60;   // calculate the width of a second on the timeline

    desiredScrollPosition += minuteWidth * currentMinute;

    desiredScrollPosition += secondWidth * currentSecond + 5;

    const scrollableDiv = scrollableDivRef.current;

    // Scroll to the desired position
    if (scrollableDiv) {
      scrollableDiv.scrollLeft = desiredScrollPosition;
    }
  };

  // scroll listener for job list
  useEffect(() => {

    // current position of scroll
    const el = jobListRef.current;
    if (!el) return;

    const handleScroll = () => {

      // check if at end
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 150;

      if (atEnd) {

        // add 2 hours = add 4 slots (30 minutes each)
        dispatch(increaseTimelineSlots({
          hoursToAdd: 2,
          authority: dbUser?.details?.authority
        }));
      }

      // check scrolling left
      let atStart = el.scrollLeft <= 50;

      if (atStart) {
        dispatch(increaseTimelineStart(2));
      }


    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);


  //#region resizable controllers

  const timelineMinimumHeight = 600;

  const [dimensions, setDimensions] = useState({
    width: window.width,
    height: timelineMinimumHeight,
  });

  const EXPANDED_LOCATIONS_WIDTH = 85;
  const EXPANDED_LOCATIONS_TABLE_WIDTH = 65;
  const EXPANDED_LIVEMAP_WIDTH = 50;
  const MIN_LEFT_WIDTH = 30;
  const MAX_LEFT_WIDTH = 96;

  const leftWidthPercent = useSelector(selectTimelineLeftWidthPercent) ?? 94;
  const isDragging = useRef(false);
  const rowRef = useRef(null);

  const onDragHandleMouseDown = (e) => {

    e.preventDefault();
    isDragging.current = true;

    const onMouseMove = (e) => {
      if (!isDragging.current || !rowRef.current) {
        return;
      }
      const rowRect = rowRef.current.getBoundingClientRect();
      const newPercent = ((e.clientX - rowRect.left) / rowRect.width) * 100;
      const maxWidth = locationsTableView ? 70 : MAX_LEFT_WIDTH;
      const clampedPercent = Math.min(maxWidth, Math.max(MIN_LEFT_WIDTH, newPercent));
      dispatch(setTimelineLeftWidthPercent(clampedPercent));
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const prevExpandLocations = useRef(expandLocations);
  const prevExpandLiveMap = useRef(expandLiveMap);
  const prevLocationsTableView = useRef(locationsTableView);

  useEffect(() => {
    const locChanged = expandLocations !== prevExpandLocations.current;
    const mapChanged = expandLiveMap !== prevExpandLiveMap.current;
    const tableChanged = locationsTableView !== prevLocationsTableView.current;
    prevExpandLocations.current = expandLocations;
    prevExpandLiveMap.current = expandLiveMap;
    prevLocationsTableView.current = locationsTableView;

    if (!locChanged && !mapChanged && !tableChanged) return;

    if (expandLocations && locationsTableView) dispatch(setTimelineLeftWidthPercent(EXPANDED_LOCATIONS_TABLE_WIDTH));
    else if (expandLocations) dispatch(setTimelineLeftWidthPercent(EXPANDED_LOCATIONS_WIDTH));
    else if (expandLiveMap) dispatch(setTimelineLeftWidthPercent(EXPANDED_LIVEMAP_WIDTH));
    else dispatch(setTimelineLeftWidthPercent(94));
  }, [expandLocations, expandLiveMap, locationsTableView]);

  // vertical resize: timeline card vs job list
  const TOTAL_CONTENT_VH = 90;
  const MIN_TOP_VH = 15;
  const MAX_TOP_VH = TOTAL_CONTENT_VH - 10;
  const TOOLBAR_HEIGHT_PX = 60;

  const topHeightVh = useSelector(selectTimelineTopHeightVh) ?? 45;
  const isDraggingVertical = useRef(false);

  const bottomHeightCalc = `calc(${TOTAL_CONTENT_VH - topHeightVh}vh - ${TOOLBAR_HEIGHT_PX}px)`;

  const onVerticalDragMouseDown = (e) => {
    e.preventDefault();
    isDraggingVertical.current = true;

    const onMouseMove = (e) => {
      if (!isDraggingVertical.current) return;
      const vhPerPx = 100 / window.innerHeight;
      const newVh = e.clientY * vhPerPx;
      dispatch(setTimelineTopHeightVh(Math.min(MAX_TOP_VH, Math.max(MIN_TOP_VH, newVh))));
    };

    const onMouseUp = () => {
      isDraggingVertical.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  //#endregion

  //#region timeline height monitor
  const timelineRef = useRef(null)
  const locationsRef = useRef(null)

  // set location height to match timeline job height
  useEffect(() => {
    // validate
    if (!timelineRef.current || !locationsRef.current) return;
    setTimelineHeight(timelineRef.current.clientHeight)
  }, [timelineRef.current?.clientHeight]);

  useEffect(() => {
    const isTouchScreenDevice = () => {
      try {
        document.createEvent('TouchEvent');
        return true;
      } catch (e) {
        return false;
      }
    };

    dispatch(setTouchScreen(isTouchScreenDevice()));
  }, [dispatch]);

  //#endregion

  const handleClickTimelineSpace = () => {
    if (showTimelineSidebar) dispatch(setShowTimelineSidebar(false))
  }

  return (
    <div style={{ backgroundColor: usedTheme.BACKGROUND_COLOR, overflow: 'hidden' }} ref={timelineMainRef}>

      {/* Global tooltip */}
      <ToolTippy selectors={['[data-tippy-content]']} />

      {showTimelineSidebar && <TimelineSidebar />}

      <TimelineContainer onClick={handleClickTimelineSpace}>

        {/* header back button / date range picker / booking filter*/}
        <TimelineTitles timelineRef={timelineMainRef} />

        {/* timeline container */}
        <Row ref={rowRef} style={{ width: '100%', display: 'flex', flexWrap: 'nowrap', overflow: 'hidden' }}>

          {/* timeline */}
          <Col
            ref={timelineRef}
            className="box"
            style={{
              flexShrink: 0,
              transition: isDragging.current ? 'none' : 'width 0.3s ease',
              width: `calc(${leftWidthPercent}% - 3px)`,
              padding: 0,
            }}
          >

            {/* timeline employee content — flex column + minHeight:0 so height:100% scroll areas work */}
            <Row
              style={{
                height: `${topHeightVh}vh`,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                transition: isDraggingVertical.current ? 'none' : 'height 0.2s ease',
              }}
            >

              <TimelineCard height="100%">

                {/* toolbar */}
                <TimelineToolbar />

                {/* timeline contents */}
                <TimelineContentContainer devRef={scrollableDivRef} onDevScroll={handleEmployeeListScroll} maxHeight={'100%'}>

                  {/* hour number display */}
                  <TimelineHourRow />

                  {/* listing of each employee timeline row */}
                  <EmployeeList clickedColumn={clickedColumn} setClickedColumn={setClickedColumn} paginationHeigh={dimensions.height + 'px'} setDimensions={setDimensions} scrollParentRef={scrollableDivRef} employeeIdsToListenTo={[]} />

                </TimelineContentContainer>

              </TimelineCard>
            </Row>

            {/* vertical drag handle to resize timeline card vs job list */}
            <div
              onMouseDown={onVerticalDragMouseDown}
              style={{
                height: '22px',
                cursor: 'row-resize',
                background: isDraggingVertical.current ? usedTheme?.BORDER_COLOR || '#888' : 'transparent',
                flexShrink: 0,
                zIndex: 10,
                transition: 'background 0.2s ease',
                margin: '2px 0',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = usedTheme?.BORDER_COLOR || '#888'; }}
              onMouseLeave={(e) => { if (!isDraggingVertical.current) e.currentTarget.style.background = 'transparent'; }}
            />

            {/* filtering toolbar */}
            <Row style={{}}>
              <JobListToolbar
                setShowCreateModal={() => modalsRef.current?.openCreateModal()}
                handleGetBookings={handleGetBookings}
                handleGetFlights={handleGetFlights}
                openEmployeeSettings={() => modalsRef.current?.openEmployeeSettings()}
                scrollableRefs={{ employeeListRef: scrollableDivRef, jobListRef: jobListRef }}
              />
            </Row>

            {/* job list — minHeight:0 lets the list shrink so overflow scroll works inside JobListContainer */}
            <Row
              className="mt-2"
              style={{
                height: iata === 'JER' ? 'auto' : bottomHeightCalc,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                overflow: iata === 'JER' ? 'visible' : 'hidden',
                transition: isDraggingVertical.current ? 'none' : 'height 0.2s ease',
              }}
            >
              <JobListContainer devRef={jobListRef} onDevScroll={handleJobListScroll} >
                <TimelineJobs disableScrollSync={disableScrollSync} handleGetBookings={handleGetBookings} />
              </JobListContainer>
            </Row>

          </Col>

          {/* drag handle to resize columns */}
          <div
            onMouseDown={onDragHandleMouseDown}
            style={{
              width: '6px',
              cursor: 'col-resize',
              background: isDragging.current ? usedTheme?.BORDER_COLOR || '#888' : 'transparent',
              flexShrink: 0,
              zIndex: 10,
              transition: 'background 0.2s ease',
              //borderStyle: 'solid', borderWidth:1,
              //backgroundColor:'red'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = usedTheme?.BORDER_COLOR || '#888'; }}
            onMouseLeave={(e) => { if (!isDragging.current) e.currentTarget.style.background = 'transparent'; }}
          />

          {/* airport locations panel - handles expand/collapse internally */}
          <Col
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.5em',
              flex: expandLocations ? 1 : '0 0 auto',
              minWidth: expandLocations ? 0 : undefined,
              maxWidth: expandLocations ? '9999px' : '50px',
              transition: isDragging.current ? 'none' : 'max-width 0.3s ease',
              overflow: 'hidden',
              height: timelineHeight || 'auto',
              padding: 0,
            }}
          >

            {/* location list view */}
            <div style={{ height: '100%', width: '100%', overflow: 'auto' }}>
              <AirportLocationsPanel timelineHeight={timelineHeight} ref={locationsRef} />
            </div>

          </Col>

        </Row>

      </TimelineContainer>

      {/* modals, context menus, toast — isolated component with its own selectors */}
      <TimelineModals
        ref={modalsRef}
        clickedColumn={clickedColumn}
        setClickedColumn={setClickedColumn}
        handleGetBookings={handleGetBookings}
        handleGetFlights={handleGetFlights}
      />

    </div>
  );

};

export default TimelineComponent;